import { sessionGeneration, sessionSignal, sessionCancelled, onSessionEnd } from "../services/sessionLifecycle.js";
import axios from "axios";
import {cacheAdapterEnhancer} from "axios-extensions";
import {BASE_URL, GOOGLE_SIGNUP_CONTINUE_URL, LOGIN_URL} from "./url";
import adapter from "axios/lib/adapters/xhr";
import $store from "@/store";
import {reAuthenticate, waitForAuthComplete} from "@/misc/auth";
import { showServerErrorModal, showConnectivityErrorModal, isAbortedRequest, isConnectivityError } from "@/misc/utils";
import { redirectOnboardingRequired } from "./onboardingRedirect";
import { shouldUseCache } from "./cachePolicy";

axios.defaults.adapter = adapter;

const cacheConfig = { cacheable: shouldUseCache };

const httpClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Cache-Control": "no-cache",
    "Content-Type": "application/json",
    "Accept": "application/json"
  },
  adapter: cacheAdapterEnhancer(axios.defaults.adapter, cacheConfig),
});

onSessionEnd(() => {
  httpClient.defaults.adapter = cacheAdapterEnhancer(axios.defaults.adapter, cacheConfig);
});

// This interceptor sets the authtoken automatically if it exists in the store
const authInterceptor = async (config) => {
  // if the url is not the login url, wait for auth to complete
  const generation = sessionGeneration();
  if (!config.skipAuthWait && !config.url.includes(LOGIN_URL) && !config.url.includes(GOOGLE_SIGNUP_CONTINUE_URL)) {
    await waitForAuthComplete();
  }
  if (generation !== sessionGeneration()) throw sessionCancelled();
  config.sessionGeneration = generation;
  if (!config.detachedSession) {
    config.signal = config.signal ? AbortSignal.any([config.signal, sessionSignal()]) : sessionSignal();
  }
  const token = config.detachedSession ? config.sessionToken : $store.state.auth.token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};


// Interceptor for responses
const responseInterceptor = (response) => {
  if (!response.config.detachedSession && response.config.sessionGeneration !== sessionGeneration()) throw sessionCancelled();
  return response;
};

// interceptor to catch errors
const errorInterceptor = async (error) => {
  if (error.config && !error.config.detachedSession && error.config.sessionGeneration !== sessionGeneration()) return Promise.reject(sessionCancelled());
  if (error.config?.sessionManaged) return Promise.reject(error);
  if (isAbortedRequest(error)) {
    return Promise.reject(error);
  }

  if (!error.response) {
    try {
      if (isConnectivityError(error)) {
        await showConnectivityErrorModal(error);
      } else {
        await showServerErrorModal(error);
      }
    }
    catch (e) {
      console.error("Error showing error modal:", e);
    }
    return Promise.reject(error);
  }

  redirectOnboardingRequired(error);

  const skipReauth = error.config?.skipAuthReauth === true;

  // all the other error responses
  switch (error.response.status) {
    case 401:
      if (skipReauth) {
        return Promise.reject(error);
      }
      return reAuthenticate(error);
    default:
      console.error(error);
  }
  return Promise.reject(error);
};
httpClient.interceptors.request.use(authInterceptor);
httpClient.interceptors.response.use(responseInterceptor, errorInterceptor);
export default httpClient;
